/**
 * 4-A SKIN+ Luxury Motion & Scroll Reveal Controller
 * Handles IntersectionObserver animations, staggered reveals, and interactive micro-effects.
 */

(function () {
  'use strict';

  // Mark motion system as ready on html root
  document.documentElement.classList.add('js-motion-ready');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initMotionSystem() {
    if (prefersReducedMotion) {
      document.querySelectorAll('.reveal-on-scroll, .reveal-fade, .reveal-scale, .shopify-section, .hero, .product-card, .collection-card').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }

    setupStaggerIndices();
    setupHeroEntrance();
    setupScrollObserver();
    setupHeaderScroll();
  }

  /**
   * Automatically compute and assign stagger index (--reveal-index) to sibling items in grids and hero blocks
   */
  function setupStaggerIndices() {
    // Hero content items
    document.querySelectorAll('.hero__content-wrapper').forEach((content) => {
      const children = Array.from(content.children).filter((el) => el.tagName !== 'STYLE' && el.tagName !== 'SCRIPT');
      children.forEach((child, index) => {
        child.style.setProperty('--reveal-index', index);
      });
    });

    const gridSelectors = [
      '.collection-list__grid',
      '.resource-list__items',
      '.resource-list--grid',
      '.product-grid',
      '.luxury-benefits-inline__grid',
      '#a-skin-customer-reviews-v1-2 .review-grid',
      '.grid',
      '.accordion-custom'
    ];

    gridSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((grid) => {
        const children = Array.from(grid.children).filter((el) => el.tagName !== 'STYLE' && el.tagName !== 'SCRIPT' && el.tagName !== 'LINK');
        children.forEach((child, index) => {
          child.style.setProperty('--reveal-index', index % 8);
          if (!child.classList.contains('reveal-on-scroll')) {
            child.classList.add('reveal-on-scroll');
          }
        });
      });
    });
  }

  /**
   * Hero entrance animation
   */
  function setupHeroEntrance() {
    const heroSections = document.querySelectorAll('.hero, [id*="hero"]');
    heroSections.forEach((hero) => {
      requestAnimationFrame(() => {
        hero.classList.add('is-revealed');
      });
    });
  }

  /**
   * IntersectionObserver for scroll-triggered reveals
   */
  function setupScrollObserver() {
    const targetSelectors = [
      '#MainContent > .shopify-section:not([id*="header"]):not([id*="announcement"])',
      '.reveal-on-scroll',
      '.reveal-fade',
      '.reveal-scale',
      '.product-card',
      '.collection-card',
      '.resource-list__item',
      '.luxury-benefits-inline__item',
      '#a-skin-why-4a-v1-0 .why-media',
      '#a-skin-why-4a-v1-0 .why-content',
      '#a-skin-why-4a-v1-0 .why-inner',
      '#a-skin-customer-reviews-v1-2 .review-card'
    ];

    const targets = document.querySelectorAll(targetSelectors.join(', '));

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -40px 0px',
      threshold: 0.05
    };

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, observerOptions);

    targets.forEach((target) => {
      const rect = target.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < vh - 20 && rect.bottom > 0) {
        target.classList.add('is-revealed');
      } else {
        observer.observe(target);
      }
    });
  }

  /**
   * Header scroll state for luxury glass backdrop & shadow
   */
  function setupHeaderScroll() {
    const headerGroup = document.getElementById('header-group') || document.querySelector('header');
    if (!headerGroup) return;

    let ticking = false;

    function getScrollTop() {
      const wrapper = document.querySelector('.page-wrapper');
      return (wrapper ? wrapper.scrollTop : 0) || window.pageYOffset || document.documentElement.scrollTop || 0;
    }

    function onScroll() {
      const scrollY = getScrollTop();
      if (scrollY > 20) {
        headerGroup.classList.add('header--scrolled');
      } else {
        headerGroup.classList.remove('header--scrolled');
      }
      ticking = false;
    }

    const scrollTarget = document.querySelector('.page-wrapper') || window;
    scrollTarget.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });

    onScroll();
  }

  // Initialize on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMotionSystem);
  } else {
    initMotionSystem();
  }

  // Support Shopify Theme Editor dynamic reloads
  document.addEventListener('shopify:section:load', () => {
    initMotionSystem();
  });
  document.addEventListener('shopify:section:reorder', () => {
    initMotionSystem();
  });

})();
